import { MouseEvent } from 'react';
import styled from 'styled-components';
import { coreTokens } from '../../core';
import { VisuallyHidden } from '../visually_hidden';
import { TableTruncatedContent } from './_table_truncated_content.component';
import { TableAlign, TableColumn, TableProps } from './table.component';
import { theme } from './table.theme';

const INTERACTIVE_SELECTOR = 'button, a[href], input, select, textarea, [role="button"], [contenteditable="true"]';

function isInteractiveTarget(target: EventTarget | null): boolean {
	return target instanceof Element && target.closest(INTERACTIVE_SELECTOR) !== null;
}

function justifyContentForCellAlign(align: TableAlign): string {
	if (align === 'center') return 'center';
	if (align === 'right') return 'flex-end';
	return 'flex-start';
}

export const StyledRow = styled.tr`
	border-bottom: 1px solid ${theme.getValue('rowBorderColor')};

	&[data-clickable] {
		cursor: pointer;

		&:hover {
			background: ${theme.getValue('rowHoverBackground')};
		}
		&:active {
			background: ${theme.getValue('rowActiveBackground')};
		}

		// The row itself isn't focusable - a visually-hidden button at the end
		// of the row is (see \`TableRow\`) - so the row's focus ring tracks that
		// button specifically, not any interactive element a cell may render.
		&:has([data-row-action]:focus-visible) {
			outline: 2px solid ${theme.getValue('focusRingColor')};
			outline-offset: -2px;
		}
	}
`;

export const StyledCell = styled.td<{ $align: TableAlign }>`
	box-sizing: border-box;
	height: ${coreTokens.size.x6};
	vertical-align: middle;
	padding: ${coreTokens.spacing.x1} ${coreTokens.spacing.x1_5};
	font: ${theme.getValue('bodyFont')};
`;

export const StyledCellInner = styled.div<{ $align: TableAlign }>`
	display: flex;
	align-items: center;
	width: 100%;
	justify-content: ${(props) => justifyContentForCellAlign(props.$align)};
`;

export function TableRow<TRow>({
	row,
	columns,
	onRowClick,
	getRowActionLabel,
}: {
	row: TRow;
	columns: TableColumn<TRow>[];
	onRowClick: TableProps<TRow>['onRowClick'];
	getRowActionLabel: TableProps<TRow>['getRowActionLabel'];
}): JSX.Element {
	function handleClick(event: MouseEvent<HTMLTableRowElement>): void {
		if (!onRowClick || isInteractiveTarget(event.target)) {
			return;
		}
		onRowClick(row, event.ctrlKey || event.metaKey);
	}

	return (
		<StyledRow data-clickable={onRowClick ? '' : undefined} onClick={onRowClick ? handleClick : undefined}>
			{columns.map((column, columnIndex) => {
				const isLastColumn = columnIndex === columns.length - 1;

				return (
					<StyledCell key={column.key} $align={column.align ?? 'left'}>
						<StyledCellInner $align={column.align ?? 'left'} style={{ minWidth: column.minWidth, maxWidth: column.maxWidth }}>
							{column.truncate ? (
								<TableTruncatedContent tooltip={column.renderTooltip?.(row)}>{column.render(row)}</TableTruncatedContent>
							) : (
								column.render(row)
							)}
							{/*
							 * A row is only an accessible action if it exposes one: a
							 * visually-hidden, labeled button at the end of the row gives
							 * keyboard/screen-reader users a real action to activate,
							 * rather than a focusable <tr> with no announced name/purpose.
							 */}
							{isLastColumn && onRowClick && getRowActionLabel ? (
								<VisuallyHidden>
									<button type='button' data-row-action onClick={() => onRowClick(row, false)}>
										{getRowActionLabel(row)}
									</button>
								</VisuallyHidden>
							) : null}
						</StyledCellInner>
					</StyledCell>
				);
			})}
		</StyledRow>
	);
}
